import React, { useCallback, useContext, useEffect, useState } from 'react'

import AirportDropdownOption from './AirportDropdownOption'
import NoAirportsOption from './NoAirportsOption'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import { LuxKitModalBehaviourStateContext } from 'LuxKit/Modal/context/LuxKitModalBehaviourContexts'
import Heading from 'LuxKit/Typography/Heading'
import SearchFormModalTextField from 'components/SearchV2/Components/SearchFormField/SearchFormModalTextField'
import useAirportSearch from 'hooks/Flights/useAirportSearch'
import useAirportSuggestions from 'hooks/Flights/useAirportSuggestions'
import noop from 'lib/function/noop'
import { getAirportDisplayText } from 'lib/flights/flightUtils'

const Results = styled.div`
  position: relative;
  min-height: ${rem(80)};
`

const InputArea = styled(Pane)`
  position: sticky;
  top: 0;
  padding: ${rem(12)} ${rem(16)};
  margin: 0 ${rem(-16)};
  z-index: 1;
`

interface Props {
  className?: string;
  onSelectAirport: (airport?: App.AirportLocation) => void;
  onChange?: (text?: string) => void;
  required?: boolean;
  requiredErrorMessage?: string;
  placeholder?: string;
  /**
   * The selected airport for this control
   */
  airport?: App.AirportLocation;
  /**
   * The airport going the *other direction*. Will be excluded from search results
   */
  alternateAirport?: App.AirportLocation;
  direction: 'departure' | 'arrival';
  fallbackAirports?: Array<App.AirportLocation>;
}

function FlightSearchAirportModalSelect(props: Props) {
  const {
    airport,
    alternateAirport,
    placeholder = 'Search by city or airport',
    className,
    onSelectAirport,
    onChange = noop,
    required,
    requiredErrorMessage,
    direction,
    fallbackAirports,
  } = props

  const suggestions = useAirportSuggestions({ pinnedAirports: fallbackAirports, excludeAirport: alternateAirport })

  const [searchPhrase, setSearchPhrase] = useState<string | undefined>()
  const [airports, loading] = useAirportSearch(searchPhrase, { excludedAirport: alternateAirport })

  useEffect(() => {
    if (airport) {
      setSearchPhrase(getAirportDisplayText(airport))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onTextChange = useCallback((text?: string) => {
    setSearchPhrase(text)
    onChange(text)
  }, [onChange])

  const onClear = useCallback(() => {
    setSearchPhrase(undefined)
    onSelectAirport(undefined)
  }, [onSelectAirport])

  const showSearch = !!(searchPhrase && searchPhrase.length > 2)
  const noAirportOptions = !loading && airports.length === 0 && showSearch
  const airportList = showSearch ? airports : suggestions.airports
  const { bodyHasReachedStart } = useContext(LuxKitModalBehaviourStateContext)

  const errorMessage = requiredErrorMessage ?? (direction === 'arrival' ? 'Please enter a destination' : 'Please enter a departure')

  return (
    <div className={className}>
      <InputArea shadow={!bodyHasReachedStart ? 'bottom-small' : undefined}>
        <SearchFormModalTextField
          value={getAirportDisplayText(airport)}
          placeholder={placeholder}
          required={required}
          searchPhrase={searchPhrase}
          requiredErrorMessage={errorMessage}
          onChange={onTextChange}
          onClear={onClear}
          autoFocus
          autoSelect
        />
      </InputArea>
      <Results>
        <LoadingIndicator visible={loading} floating position="top">
          Loading
        </LoadingIndicator>
        {!showSearch && airportList.length > 0 && <Heading variant="heading6">{suggestions.heading}</Heading>}
        {!loading && airportList.map(airport =>
          <AirportDropdownOption
            key={airport.id}
            subRecord={showSearch && airport.isCityAirport}
            airport={airport}
            onSelectAirport={onSelectAirport}
          />,
        )}
        {noAirportOptions && <NoAirportsOption inputValue={searchPhrase} />}
      </Results>
    </div>
  )
}

export default FlightSearchAirportModalSelect
